import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { FileSignature, Loader2, Send, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const fmtIt = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");

export const PendingLettersPanel = ({ onLoaded }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [includeSigned, setIncludeSigned] = useState(false);
    const [sending, setSending] = useState(null);
    const [open, setOpen] = useState(true);

    const load = async (withSigned = includeSigned) => {
        setLoading(true);
        try {
            const { data } = await api.get("/delivery/compliance/letters", { params: { include_signed: withSigned } });
            setData(data);
            onLoaded && onLoaded(data);
        } catch {
            toast.error("Errore caricamento comunicazioni da firmare");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const remind = async (row) => {
        setSending(row.document_id);
        try {
            const { data: r } = await api.post(`/delivery/compliance/letters/${row.document_id}/remind`);
            toast.success(`Sollecito inviato a ${r.to}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invio sollecito non riuscito");
        } finally {
            setSending(null);
        }
    };

    const items = data?.items || [];
    return (
        <div className="mb-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="pending-letters-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left" data-testid="pending-letters-toggle">
                    <FileSignature className="h-5 w-5 text-[#376DB3]" />
                    <div>
                        <div className="text-sm font-semibold text-slate-800">
                            Comunicazioni proroga / trasformazione da firmare
                            {data && (
                                <Badge className={`ml-2 ${data.pending ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}`} data-testid="pending-letters-count">
                                    {data.pending} in attesa · {data.signed} firmate
                                </Badge>
                            )}
                        </div>
                        <div className="text-xs text-slate-500">Sollecito email automatico ogni 3 giorni (max 10) finché il dipendente non firma per accettazione sul portale. I cessati restano sollecitabili finché il loro accesso al portale è attivo.</div>
                    </div>
                    {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={includeSigned} onChange={(e) => { setIncludeSigned(e.target.checked); load(e.target.checked); }} className="h-3.5 w-3.5" data-testid="pending-letters-include-signed" />
                    Mostra anche firmate
                </label>
            </div>
            {open && (
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead>Dipendente</TableHead>
                            <TableHead>Comunicazione</TableHead>
                            <TableHead className="text-center">Consegnata</TableHead>
                            <TableHead className="text-center">Attesa</TableHead>
                            <TableHead className="text-center">Solleciti</TableHead>
                            <TableHead className="text-right">Stato</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500" data-testid="pending-letters-empty">
                                Nessuna comunicazione in attesa di firma.
                            </TableCell></TableRow>
                        ) : items.map((r) => (
                            <TableRow key={r.document_id} data-testid={`pending-letter-${r.document_id}`}>
                                <TableCell>
                                    <Link to={`/dipendenti/${r.employee_id}`} className="font-medium text-[#376DB3] hover:underline">{r.employee_label}</Link>
                                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                        matr. {r.matricola || "—"}
                                        {r.cessato && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] uppercase text-amber-700" data-testid={`pending-letter-cessato-${r.document_id}`}>Cessato {fmtIt(r.data_cessazione)}</Badge>}
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm">
                                    <Badge variant="outline" className={`mr-2 text-[10px] ${r.kind === "proroga" ? "border-sky-300 text-sky-700" : "border-teal-300 text-teal-700"}`}>{r.kind === "proroga" ? "PROROGA" : "TRASFORMAZIONE"}</Badge>
                                    {r.title}
                                </TableCell>
                                <TableCell className="text-center font-mono-numbers text-sm">{fmtIt(r.uploaded_at)}</TableCell>
                                <TableCell className="text-center font-mono-numbers text-sm">
                                    {r.signed ? "—" : <span className={r.days_pending > 7 ? "font-semibold text-red-600" : ""}>{r.days_pending ?? "—"} gg</span>}
                                </TableCell>
                                <TableCell className="text-center text-xs text-slate-600">
                                    {r.reminders_sent}{r.last_reminder_at ? <div className="text-[10px] text-slate-400">ultimo {fmtIt(r.last_reminder_at)}</div> : null}
                                </TableCell>
                                <TableCell className="text-right">
                                    {r.signed ? (
                                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Firmata {fmtIt(r.signed_at)}</Badge>
                                    ) : (
                                        <div className="flex flex-col items-end gap-0.5">
                                            <Button size="sm" variant="outline" onClick={() => remind(r)} disabled={sending === r.document_id || !r.notifiable} className="h-7 gap-1.5 text-xs" title={r.notifiable ? "Invia subito un sollecito email" : r.not_notifiable_reason} data-testid={`pending-letter-remind-${r.document_id}`}>
                                                {sending === r.document_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                                Sollecita ora
                                            </Button>
                                            {!r.notifiable && <span className="text-[10px] text-slate-400" data-testid={`pending-letter-reason-${r.document_id}`}>{r.not_notifiable_reason}</span>}
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};
